import React from "react"
import type { ErrorRouteComponent } from "@tanstack/react-router"
import * as Sentry from "@sentry/react"
import { Button } from "@/components/ui/button"
import { getChunkLoadErrorSignature, isRecoverableChunkLoadError } from "@/utils/chunkLoadError"

const CHUNK_ERROR_RECOVERY_KEY = "fyp:last-chunk-load-recovery-signature"

/**
 * Application-wide router error UI with graceful recovery for stale chunk failures.
 *
 * @param props - Router error component props
 * @returns A user-friendly recovery panel
 */
export const RouterErrorFallback: ErrorRouteComponent = ({ error, reset }) => {
  const isChunkLoadError = isRecoverableChunkLoadError(error)
  const [hasAttemptedRecovery, setHasAttemptedRecovery] = React.useState(false)

  React.useEffect(() => {
    // Report non-chunk-load errors to Sentry
    if (!isChunkLoadError) {
      Sentry.captureException(error)
    }
  }, [error, isChunkLoadError])

  React.useEffect(() => {
    if (!isChunkLoadError) {
      return
    }

    const chunkErrorSignature = getChunkLoadErrorSignature(error)
    const lastRecoveredSignature = sessionStorage.getItem(CHUNK_ERROR_RECOVERY_KEY)

    if (lastRecoveredSignature === chunkErrorSignature) {
      setHasAttemptedRecovery(true)
      return
    }

    sessionStorage.setItem(CHUNK_ERROR_RECOVERY_KEY, chunkErrorSignature)
    globalThis.location.reload()
  }, [error, isChunkLoadError])

  const errorMessage = error instanceof Error ? error.message : String(error)

  return (
    <div className="bg-background text-foreground flex min-h-screen items-center justify-center px-6">
      <div className="bg-card text-card-foreground w-full max-w-xl rounded-xl p-6 shadow-sm">
        <h1 className="text-xl font-semibold">Something went wrong</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          {isChunkLoadError
            ? "This tab may be using outdated app files."
            : "An unexpected error occurred while loading this page."}
        </p>

        {isChunkLoadError ? (
          <div className="mt-4 space-y-2 text-sm">
            <p>
              {hasAttemptedRecovery
                ? "Automatic recovery did not succeed. Please refresh manually."
                : "Trying to recover automatically..."}
            </p>
            <Button onClick={() => globalThis.location.reload()} type="button">
              Reload page
            </Button>
          </div>
        ) : (
          <div className="mt-4 space-y-2">
            <Button onClick={() => reset()} type="button">
              Try again
            </Button>
            <pre className="bg-muted text-destructive mt-2 overflow-auto rounded-md p-3 text-xs">
              <code>{errorMessage}</code>
            </pre>
          </div>
        )}
      </div>
    </div>
  )
}
